import { Button } from "~/atoms/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "~/atoms/dialog";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Check } from "lucide-react";
import {
  FetchError,
  type WorkflowStep,
  type ScheduleReviewPeriod,
  type DocumentDetailsVersion,
  type WorkflowDetails,
} from "~/api/types";
import { downloadDocument, updateDocumentVersion } from "~/api/v2/requests";
import {
  analyzeTrackChanges,
  type TrackChangeStatus,
} from "~/helpers/docx-track-changes";
import { Alert, AlertDescription, AlertTitle } from "~/atoms/alert";
import { getApprovalStatus } from "~/helpers/documentPermissions";
import { Label } from "~/atoms/label";
import DatePicker from "~/molecules/DatePicker";
import {
  addDays,
  addMonths,
  isPastDate,
  toISODateString,
} from "~/helpers/date";
import { toast } from "react-toastify";
import { Spinner } from "~/atoms/spinner";

type PublishModalProps = {
  onConfirm: (versionId: number) => void;
  canManage: boolean;
  selectedVersion: DocumentDetailsVersion;
  workflow: WorkflowDetails;
  lastStep: WorkflowStep | undefined;
  scheduleReviewPeriod: ScheduleReviewPeriod;
};

const PERIOD_IN_MONTHS: Record<Exclude<ScheduleReviewPeriod, null>, number> = {
  "3 MONTH": 3,
  "6 MONTH": 6,
  "1 YEAR": 12,
};

export function PublishModal({
  onConfirm,
  selectedVersion,
  workflow,
  canManage,
  lastStep,
  scheduleReviewPeriod,
}: PublishModalProps) {
  const { t } = useTranslation();
  const [trackChangeStatus, setTrackChangeStatus] =
    useState<TrackChangeStatus | null>(null);
  const [nextReviewFrom, setNextReviewFrom] = useState<Date>();
  const [nextReviewTo, setNextReviewTo] = useState<Date>();
  const [showNoReviewWarning, setShowNoReviewWarning] = useState<
    boolean | null
  >(null);

  const [publishError, setPublishError] = useState<FetchError | null>(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (!scheduleReviewPeriod) {
      setNextReviewFrom(undefined);
      setNextReviewTo(undefined);
      return;
    }

    const months = PERIOD_IN_MONTHS[scheduleReviewPeriod];
    const { reviewFrom, reviewTo } = workflow.documentVersion;

    const nextFrom = reviewFrom ? addMonths(reviewFrom, months) : undefined;
    const nextTo = reviewTo ? addMonths(reviewTo, months) : undefined;

    const resolvedFrom =
      nextFrom && !isPastDate(nextFrom) ? nextFrom : undefined;
    const resolvedTo = nextTo && !isPastDate(nextTo) ? nextTo : undefined;

    setNextReviewFrom(resolvedFrom);
    setNextReviewTo(
      resolvedFrom && resolvedTo && resolvedFrom > resolvedTo
        ? undefined
        : resolvedTo,
    );
  }, [open, scheduleReviewPeriod, workflow]);

  const handlePublishDocument = async () => {
    if (!nextReviewFrom && showNoReviewWarning === null) {
      setShowNoReviewWarning(true);
      return;
    }

    setIsPublishing(true);
    setPublishError(null);

    try {
      await updateDocumentVersion(selectedVersion.id, {
        status: "published",
        nextReviewFrom: toISODateString(nextReviewFrom),
        nextReviewTo: toISODateString(nextReviewTo),
      });

      toast.success(t(($) => $.commons.workflow.documentPublishedSuccessfully));
      setOpen(false);
      onConfirm(selectedVersion.id);
    } catch (error) {
      if (error instanceof FetchError) {
        setPublishError(error);
      }
      if (
        !(error instanceof FetchError) ||
        error.code !== "DOCUMENT_OPEN_IN_EDITOR"
      ) {
        toast.error(t(($) => $.commons.workflow.failedToPublishDocument));
      }
    } finally {
      setIsPublishing(false);
    }
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    let cancelled = false;

    const fetchTrackChangesStatus = async () => {
      if (selectedVersion.fileType !== "docx") {
        return;
      }

      const blob = await downloadDocument(selectedVersion.id).catch(() => null);
      if (!blob) {
        return;
      }

      const status = await analyzeTrackChanges(blob).catch(() => null);
      if (!status || cancelled) {
        return;
      }

      setTrackChangeStatus(status);
    };

    void fetchTrackChangesStatus();

    return () => {
      cancelled = true;
      setTrackChangeStatus(null);
    };
  }, [open, selectedVersion]);

  const handleOpenChange = (open: boolean) => {
    setOpen(open);
    if (!open) {
      setPublishError(null);
      setShowNoReviewWarning(null);
    }
  };

  const { isWaitingForApprovals, approvedCount, totalCount } = lastStep
    ? getApprovalStatus(lastStep)
    : { isWaitingForApprovals: false, approvedCount: 0, totalCount: 0 };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button className="min-w-48" disabled={isWaitingForApprovals}>
          <Check />
          {isWaitingForApprovals
            ? t(($) => $.commons.workflow.waitingForApprovals, {
                approvedCount,
                totalCount,
              })
            : t(($) => $.commons.workflow.publishDocument)}
        </Button>
      </DialogTrigger>
      <DialogContent className="gap-0 p-0">
        <DialogHeader className="p-6">
          <DialogTitle>
            {t(($) => $.commons.workflow.nextReviewPeriod)}
          </DialogTitle>
          <DialogDescription>
            {t(($) => $.commons.workflow.nextReviewPeriodDescription)}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-6 px-6 pb-6">
          {publishError?.code === "DOCUMENT_OPEN_IN_EDITOR" && (
            <Alert variant="destructive">
              <AlertTriangle />
              <AlertTitle>
                {t(($) => $.commons.workflow.documentOpenInEditorError)}
              </AlertTitle>
              <AlertDescription>
                {t(
                  ($) =>
                    $.commons.workflow.documentOpenInEditorErrorDescription,
                )}
              </AlertDescription>
            </Alert>
          )}

          {trackChangeStatus?.hasTrackRevisionsSetting && (
            <Alert variant="warn">
              <AlertTriangle />
              <AlertTitle>
                {t(($) => $.commons.workflow.trackChangesEnabledWarning)}
              </AlertTitle>
              <AlertDescription>
                {t(
                  ($) =>
                    $.commons.workflow.trackChangesEnabledWarningDescription,
                )}
              </AlertDescription>
            </Alert>
          )}

          {trackChangeStatus?.hasRevisionNodes && (
            <Alert variant="warn">
              <AlertTriangle />
              <AlertTitle>
                {t(($) => $.commons.workflow.unresolvedSuggestionsWarning)}
              </AlertTitle>
              <AlertDescription>
                {t(
                  ($) =>
                    $.commons.workflow.unresolvedSuggestionsWarningDescription,
                )}
              </AlertDescription>
            </Alert>
          )}

          {trackChangeStatus?.hasCommentNodes && (
            <Alert variant="warn">
              <AlertTriangle />
              <AlertTitle>
                {t(($) => $.commons.workflow.commentsWarning)}
              </AlertTitle>
              <AlertDescription>
                {t(($) => $.commons.workflow.commentsWarningDescription)}
              </AlertDescription>
            </Alert>
          )}

          {showNoReviewWarning && (
            <Alert variant="warn">
              <AlertTriangle />
              <AlertTitle>
                {canManage
                  ? t(($) => $.commons.workflow.missingReviewDates)
                  : t(($) => $.commons.workflow.noReviewSchedule)}
              </AlertTitle>
              <AlertDescription>
                {canManage
                  ? t(($) => $.commons.workflow.missingReviewDatesDescription)
                  : t(($) => $.commons.workflow.noReviewScheduleDescription)}
              </AlertDescription>
            </Alert>
          )}

          <div className="flex flex-col gap-2">
            <div>
              <Label>{t(($) => $.commons.workflow.startDate)}</Label>
              <DatePicker
                value={nextReviewFrom}
                onChange={(date) => {
                  setNextReviewFrom(date);
                  setShowNoReviewWarning(null);
                  if (!date || (nextReviewTo && date >= nextReviewTo)) {
                    setNextReviewTo(undefined);
                  }
                }}
                minDate={addDays(new Date(), 1)}
              />
            </div>
            <div>
              <Label>{t(($) => $.commons.workflow.endDate)}</Label>
              <DatePicker
                value={nextReviewTo}
                onChange={setNextReviewTo}
                minDate={
                  nextReviewFrom
                    ? addDays(nextReviewFrom, 1)
                    : addDays(new Date(), 2)
                }
                disabled={!nextReviewFrom}
              />
            </div>
          </div>

          <DialogFooter className="flex flex-row justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isPublishing}
            >
              {t(($) => $.commons.workflow.cancel)}
            </Button>
            <Button onClick={handlePublishDocument} disabled={isPublishing}>
              {isPublishing ? (
                <Spinner className="text-primary-foreground size-5"></Spinner>
              ) : (
                t(($) => $.commons.workflow.publishDocument)
              )}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
